import { useState, useEffect, useRef } from 'react'
import { Menu, X, ChevronDown, Calendar, MapPin, Sparkles, User, Camera, Coffee, Heart, Briefcase, PartyPopper } from 'lucide-react'
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from 'framer-motion'
import { Link, useLocation } from 'react-router-dom'
import { rooms } from '../data/rooms'

interface NavItem {
  name: string
  href: string
  hasDropdown?: boolean
  dropdownType?: 'rooms' | 'pages' | 'events'
  icon?: React.ReactNode
}

const mainNavItems: NavItem[] = [
  { name: 'Home', href: '/' },
  { name: 'Ihre Veranstaltungen', href: '/veranstaltungen', hasDropdown: true, dropdownType: 'events', icon: <Calendar className="w-4 h-4" /> },
  { name: 'Unsere Räume', href: '/raeume', hasDropdown: true, dropdownType: 'rooms', icon: <MapPin className="w-4 h-4" /> },
  { name: 'Brunch', href: '/brunch', icon: <Coffee className="w-4 h-4" /> },
  { name: 'Events & Partys', href: '/partys', icon: <Sparkles className="w-4 h-4" /> },
  { name: 'Galerie', href: '/galerie', icon: <Camera className="w-4 h-4" /> },
  { name: 'Über Uns', href: '/geschichte', hasDropdown: true, dropdownType: 'pages', icon: <User className="w-4 h-4" /> },
]

const eventDropdownItems = [
  { name: 'Hochzeiten', href: '/hochzeiten', description: 'Ihr schönster Tag', icon: Heart },
  { name: 'Firmenfeiern', href: '/unternehmensfeiern', description: 'Professionell & beeindruckend', icon: Briefcase },
  { name: 'Private Feiern', href: '/private-feiern', description: 'Unvergessliche Momente', icon: PartyPopper },
]

const aboutDropdownItems = [
  { name: 'Geschichte', href: '/geschichte', description: 'Erfahren Sie mehr über unsere Tradition' },
  { name: 'Buffets & Angebote', href: '/buffets-angebote', description: 'Unsere Buffetvorschläge und Angebote' },
  { name: 'Foodtruck', href: '/foodtruck', description: 'Unser mobiler Genuss' },
  { name: 'Jobs', href: '/jobs', description: 'Teil des Teams werden' },
]

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null)
  const [hoveredRoom, setHoveredRoom] = useState<string | null>(null)
  const location = useLocation()
  const dropdownRef = useRef<HTMLDivElement>(null)
  const navRef = useRef<HTMLElement>(null)

  const isHomePage = location.pathname === '/'

  // Navbar hat immer den gleichen Style - kein unterschiedliches Verhalten
  useEffect(() => {
    setIsScrolled(true)
  }, [])

  // Body-Scroll Management
  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [isMobileMenuOpen])

  // Close Dropdown on Outside Click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setActiveDropdown(null)
      }
    }

    if (activeDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [activeDropdown])

  // Close Menus on Route Change
  useEffect(() => {
    setIsMobileMenuOpen(false)
    setActiveDropdown(null)
  }, [location])

  return (
    <>
      {/* Premium Glassmorphic Navigation */}
      <motion.nav
        ref={navRef}
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="fixed top-2 md:top-4 left-0 right-0 z-[9999] px-2 sm:px-4 lg:px-8 transition-all duration-700"
      >
          <motion.div
            initial={false}
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.85)',
              backdropFilter: 'blur(12px) saturate(150%)',
            }}
            className="max-w-7xl mx-auto rounded-3xl border border-white/10 shadow-2xl"
          >
            <div className="relative">
              {/* Subtle Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-white/[0.02] via-transparent to-white/[0.02] pointer-events-none rounded-[inherit]" />
              
              <div className="flex items-center justify-between h-16 px-4 md:px-8 transition-all duration-500">
              {/* Logo */}
              <Link to="/" className="relative z-10 flex-shrink-0">
                <motion.div
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                >
                  <motion.img
                    src="/Logo_HM.png"
                    alt="Hückels May"
                    className="h-8 md:h-10 w-auto transition-all duration-500"
                  />
                </motion.div>
              </Link>

              {/* Desktop Navigation */}
              <div className="hidden lg:flex items-center gap-1">
                {mainNavItems.map((item, index) => {
                  if (item.hasDropdown) {
                    return (
                      <div
                        key={item.href}
                        ref={item.hasDropdown ? dropdownRef : null}
                        className="relative"
                        onMouseEnter={() => setActiveDropdown(item.name)}
                        onMouseLeave={() => setActiveDropdown(null)}
                      >
                        <MagneticButton>
                          <Link
                            to={item.href}
                            className="group relative px-4 py-2.5 flex items-center gap-2"
                          >
                            {item.icon && (
                              <motion.span
                                className="text-white/60 group-hover:text-white transition-colors"
                                whileHover={{ rotate: [0, -10, 10, 0] }}
                                transition={{ duration: 0.5 }}
                              >
                                {item.icon}
                              </motion.span>
                            )}
                            <span className={`font-light uppercase tracking-[0.2em] text-white/90 group-hover:text-white transition-all duration-300 whitespace-nowrap ${
                              isScrolled ? 'text-[10px]' : 'text-xs'
                            }`}>
                              {item.name}
                            </span>
                            <motion.div
                              animate={{ rotate: activeDropdown === item.name ? 180 : 0 }}
                              transition={{ duration: 0.3 }}
                            >
                              <ChevronDown className="w-3 h-3 text-white/50 group-hover:text-white/90 transition-colors" />
                            </motion.div>
                            
                            {/* Hover Effect */}
                            <motion.div
                              className="absolute inset-0 bg-white/5 rounded-xl -z-10"
                              initial={{ opacity: 0, scale: 0.8 }}
                              whileHover={{ opacity: 1, scale: 1 }}
                              transition={{ duration: 0.3 }}
                            />
                          </Link>
                        </MagneticButton>

                        {/* Mega Dropdown */}
                        <AnimatePresence>
                          {activeDropdown === item.name && (
                            <motion.div
                              initial={{ opacity: 0, y: 20, scale: 0.95, filter: 'blur(10px)' }}
                              animate={{ opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' }}
                              exit={{ opacity: 0, y: 20, scale: 0.95, filter: 'blur(10px)' }}
                              transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
                              className="absolute top-full left-1/2 -translate-x-1/2 mt-4 z-50"
                              onMouseEnter={() => setActiveDropdown(item.name)}
                              onMouseLeave={() => setActiveDropdown(null)}
                            >
                              {item.dropdownType === 'rooms' ? (
                                <RoomsDropdown 
                                  rooms={rooms} 
                                  onClose={() => setActiveDropdown(null)}
                                  hoveredRoom={hoveredRoom}
                                  setHoveredRoom={setHoveredRoom}
                                />
                              ) : item.dropdownType === 'events' ? (
                                <EventsDropdown 
                                  items={eventDropdownItems}
                                  onClose={() => setActiveDropdown(null)}
                                />
                              ) : (
                                <PagesDropdown 
                                  items={aboutDropdownItems}
                                  onClose={() => setActiveDropdown(null)}
                                />
                              )}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    )
                  }

                  return (
                    <MagneticButton key={item.href}>
                      <Link
                        to={item.href}
                        className="group relative px-4 py-2.5 flex items-center gap-2"
                      >
                        {item.icon && (
                          <motion.span
                            className="text-white/60 group-hover:text-white transition-colors"
                            whileHover={{ rotate: [0, -10, 10, 0] }}
                            transition={{ duration: 0.5 }}
                          >
                            {item.icon}
                          </motion.span>
                        )}
                        <span className={`font-light uppercase tracking-[0.2em] text-white/90 group-hover:text-white transition-all duration-300 hidden sm:inline whitespace-nowrap ${
                          isScrolled ? 'text-[10px]' : 'text-xs'
                        }`}>
                          {item.name}
                        </span>
                        
                        {/* Hover Effect */}
                        <motion.div
                          className="absolute inset-0 bg-white/5 rounded-xl -z-10"
                          initial={{ opacity: 0, scale: 0.8 }}
                          whileHover={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.3 }}
                        />
                      </Link>
                    </MagneticButton>
                  )
                })}

                {/* Premium CTA */}
                <Link to="/kontakt">
                  <motion.button
                    style={{
                      background: 'linear-gradient(135deg, #e74c63 0%, #d32f4a 50%, #b1233c 100%)',
                      boxShadow: '0 8px 24px rgba(231, 76, 99, 0.5)',
                      border: '1px solid rgba(231, 76, 99, 0.3)',
                      position: 'relative',
                    }}
                    className={`ml-2 md:ml-4 px-4 md:px-6 py-2 md:py-2.5 text-white rounded-full font-light uppercase tracking-[0.2em] hidden sm:inline-flex items-center ${
                      isScrolled ? 'text-[10px]' : 'text-xs'
                    }`}
                    whileHover={{ scale: 1.05, boxShadow: '0 12px 32px rgba(231, 76, 99, 0.6)' }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <span style={{ position: 'relative', zIndex: 10 }}>Kontakt</span>
                  </motion.button>
                </Link>
              </div>

              {/* Mobile Menu Button */}
              <motion.button
                onClick={() => setIsMobileMenuOpen(prev => !prev)}
                className="lg:hidden relative p-2 md:p-3 text-white/90 hover:text-white transition-colors"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                aria-label={isMobileMenuOpen ? "Menü schließen" : "Menü öffnen"}
              >
                {isMobileMenuOpen ? (
                  <X className="h-5 w-5 md:h-6 md:w-6" />
                ) : (
                  <Menu className="h-5 w-5 md:h-6 md:w-6" />
                )}
              </motion.button>
            </div>
          </div>
        </motion.div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <MobileMenu
            mainNavItems={mainNavItems}
            rooms={rooms}
            aboutItems={aboutDropdownItems}
            onClose={() => setIsMobileMenuOpen(false)}
          />
        )}
      </AnimatePresence>
    </>
  )
}

// Magnetic Button Component
function MagneticButton({ children }: { children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null)
  const x = useMotionValue(0)
  const y = useMotionValue(0)
  const mouseXSpring = useSpring(x, { stiffness: 300, damping: 20 })
  const mouseYSpring = useSpring(y, { stiffness: 300, damping: 20 })

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!ref.current) return
    const rect = ref.current.getBoundingClientRect()
    const centerX = rect.left + rect.width / 2
    const centerY = rect.top + rect.height / 2
    x.set((e.clientX - centerX) * 0.15)
    y.set((e.clientY - centerY) * 0.15)
  }

  const handleMouseLeave = () => {
    x.set(0)
    y.set(0)
  }

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ x: mouseXSpring, y: mouseYSpring }}
    >
      {children}
    </motion.div>
  )
}

// Rooms Mega Dropdown
function RoomsDropdown({ 
  rooms, 
  onClose, 
  hoveredRoom, 
  setHoveredRoom 
}: { 
  rooms: any[]
  onClose: () => void
  hoveredRoom: string | null
  setHoveredRoom: (id: string | null) => void
}) {
  return (
    <div 
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        backdropFilter: 'blur(12px) saturate(150%)',
      }}
      className="w-[90vw] max-w-[680px] rounded-3xl shadow-2xl border border-white/10 p-4 md:p-6 overflow-hidden"
    >
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-5"
      >
        <h3 className="font-serif text-xl font-light text-white mb-1">Unsere Räume</h3>
        <p className="text-white/50 text-xs">Wählen Sie den perfekten Raum</p>
      </motion.div>

      {/* Rooms Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {rooms.map((room, index) => (
          <motion.div
            key={room.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.06, duration: 0.4 }}
            onMouseEnter={() => setHoveredRoom(room.id)}
            onMouseLeave={() => setHoveredRoom(null)}
          >
            <Link
              to={`/raeume/${room.slug}`}
              onClick={onClose}
              className="block group relative"
            >
              <div className="relative overflow-hidden rounded-lg mb-3 aspect-[4/3]">
                <motion.img
                  src={room.imageUrl}
                  alt={room.name}
                  className="w-full h-full object-cover"
                  whileHover={{ scale: 1.06 }}
                  transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
                />
                <div className="absolute inset-0 bg-gray-800/45 opacity-60 group-hover:opacity-80 transition-opacity duration-400" />
                
                {/* Hover Overlay */}
                <motion.div
                  className="absolute inset-0 bg-white/0 group-hover:bg-white/5 transition-colors duration-400"
                  initial={{ opacity: 0 }}
                  whileHover={{ opacity: 1 }}
                />
              </div>
              
              <h4 className="font-serif text-base font-medium text-white mb-1 group-hover:text-white/90 transition-colors">
                {room.dropdownName || room.name}
              </h4>
              <p className="text-xs text-white/50 mb-2 line-clamp-1">
                {room.subtitle}
              </p>
              
              {/* Animated Divider */}
              <motion.div
                className="h-[1px] bg-gradient-to-r from-white/20 via-white/30 to-white/20"
                initial={{ scaleX: 0.3 }}
                whileHover={{ scaleX: 1 }}
                transition={{ duration: 0.4 }}
              />
            </Link>
          </motion.div>
        ))}
      </div>

      {/* Footer */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="mt-5 pt-4 border-t border-white/10"
      >
        <Link
          to="/raeume"
          onClick={onClose}
          className="group inline-flex items-center gap-2 text-xs font-light text-white/60 hover:text-white transition-colors uppercase tracking-[0.12em]"
        >
          Alle Räume ansehen
          <motion.span
            className="inline-block"
            animate={{ x: [0, 4, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            →
          </motion.span>
        </Link>
      </motion.div>

      {/* Background Hover Effect */}
      <AnimatePresence>
        {hoveredRoom && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="absolute inset-0 pointer-events-none z-0"
          >
            {(() => {
              const room = rooms.find(r => r.id === hoveredRoom)
              if (!room) return null
              return (
                <div
                  className="absolute inset-0 bg-cover bg-center rounded-xl"
                  style={{
                    backgroundImage: `url(${room.imageUrl})`,
                    opacity: 0.05,
                    filter: 'blur(40px) grayscale(60%)',
                  }}
                />
              )
            })()}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// Events Dropdown
function EventsDropdown({ items, onClose }: { items: any[], onClose: () => void }) {
  return (
    <div 
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        backdropFilter: 'blur(12px) saturate(150%)',
      }}
      className="w-[90vw] max-w-[400px] rounded-3xl shadow-2xl border border-white/10 p-6 md:p-8"
    >
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-5"
      >
        <h3 className="font-serif text-xl font-light text-white mb-1">Was möchten Sie feiern?</h3>
        <p className="text-white/50 text-xs">Wählen Sie Ihre Veranstaltung</p>
      </motion.div>
      <div className="space-y-2">
        {items.map((item, index) => {
          const IconComponent = item.icon
          return (
            <motion.div
              key={item.href}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.08, duration: 0.4 }}
            >
              <Link
                to={item.href}
                onClick={onClose}
                className="group block p-4 rounded-xl hover:bg-white/5 transition-all duration-300"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-brand-800/20 border border-brand-800/30 flex items-center justify-center group-hover:bg-brand-800/30 transition-colors">
                    <IconComponent className="w-5 h-5 text-brand-400" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-white mb-1 group-hover:text-white/90 transition-colors uppercase tracking-[0.05em]">
                      {item.name}
                    </h4>
                    <p className="text-sm text-white/50 group-hover:text-white/60 transition-colors italic">
                      {item.description}
                    </p>
                  </div>
                </div>
              </Link>
            </motion.div>
          )
        })}
      </div>
    </div>
  )
}

// Pages Dropdown
function PagesDropdown({ items, onClose }: { items: any[], onClose: () => void }) {
  return (
    <div 
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        backdropFilter: 'blur(12px) saturate(150%)',
      }}
      className="w-[90vw] max-w-[400px] rounded-3xl shadow-2xl border border-white/10 p-6 md:p-8"
    >
      <div className="space-y-2">
        {items.map((item, index) => (
          <motion.div
            key={item.href}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.08, duration: 0.4 }}
          >
            <Link
              to={item.href}
              onClick={onClose}
              className="group block p-4 rounded-xl hover:bg-white/5 transition-all duration-300"
            >
              <h4 className="font-medium text-white mb-1 group-hover:text-white/90 transition-colors uppercase tracking-[0.05em]">
                {item.name}
              </h4>
              <p className="text-sm text-white/50 group-hover:text-white/60 transition-colors italic">
                {item.description}
              </p>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  )
}

// Mobile Menu Component
function MobileMenu({ 
  mainNavItems, 
  rooms, 
  aboutItems,
  onClose 
}: { 
  mainNavItems: NavItem[]
  rooms: any[]
  aboutItems: any[]
  onClose: () => void
}) {
  const [openDropdowns, setOpenDropdowns] = useState<Set<string>>(new Set())

  const toggleDropdown = (itemName: string) => {
    setOpenDropdowns(prev => {
      const next = new Set(prev)
      if (next.has(itemName)) {
        next.delete(itemName)
      } else {
        next.add(itemName)
      }
      return next
    })
  }

  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed inset-0 bg-black/90 backdrop-blur-md z-[100]"
        onClick={onClose}
      />

      {/* Menu Content - Slide from top */}
      <motion.div
        initial={{ opacity: 0, y: -100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -100 }}
        transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="fixed top-0 left-0 right-0 z-[110] bg-zinc-950/98 backdrop-blur-xl border-b border-white/10 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="max-h-[100vh] overflow-y-auto pt-24">
          {/* Navigation */}
          <nav className="px-4 py-6 space-y-1">
            {mainNavItems.map((item, index) => {
              const isDropdownOpen = openDropdowns.has(item.name)
              
              return (
                <motion.div
                  key={item.href}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05, duration: 0.3 }}
                >
                  {item.hasDropdown ? (
                    <div className="space-y-0">
                      {/* Main Item with Toggle */}
                      <div className="flex items-center justify-between">
                        <Link
                          to={item.href}
                          onClick={onClose}
                          className="flex items-center gap-3 flex-1 py-3 px-2 rounded-lg active:bg-white/5 transition-colors"
                        >
                          {item.icon && (
                            <span className="text-white/70">
                              {item.icon}
                            </span>
                          )}
                          <span className="text-lg font-light text-white">
                            {item.name}
                          </span>
                        </Link>
                        <motion.button
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            toggleDropdown(item.name)
                          }}
                          className="p-2 rounded-lg active:bg-white/5 transition-colors"
                          whileTap={{ scale: 0.95 }}
                          aria-label={isDropdownOpen ? "Dropdown schließen" : "Dropdown öffnen"}
                        >
                          <motion.div
                            animate={{ rotate: isDropdownOpen ? 180 : 0 }}
                            transition={{ duration: 0.3 }}
                          >
                            <ChevronDown className="w-5 h-5 text-white/60" />
                          </motion.div>
                        </motion.button>
                      </div>

                      {/* Dropdown Content */}
                      <AnimatePresence>
                        {isDropdownOpen && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
                            className="overflow-hidden"
                          >
                            <div className="pl-11 pr-2 py-2 space-y-1 border-l-2 border-white/10 ml-2">
                              {item.dropdownType === 'rooms' 
                                ? rooms.map((room, roomIndex) => (
                                    <motion.div
                                      key={room.id}
                                      initial={{ opacity: 0, x: -10 }}
                                      animate={{ opacity: 1, x: 0 }}
                                      transition={{ delay: roomIndex * 0.03, duration: 0.2 }}
                                    >
                                      <Link
                                        to={`/raeume/${room.slug}`}
                                        onClick={onClose}
                                        className="block py-2.5 px-3 rounded-lg text-base text-white/70 hover:text-white active:bg-white/5 transition-colors"
                                      >
                                        {room.dropdownName || room.name}
                                      </Link>
                                    </motion.div>
                                  ))
                                : item.dropdownType === 'events'
                                ? eventDropdownItems.map((subItem, subIndex) => (
                                    <motion.div
                                      key={subItem.href}
                                      initial={{ opacity: 0, x: -10 }}
                                      animate={{ opacity: 1, x: 0 }}
                                      transition={{ delay: subIndex * 0.03, duration: 0.2 }}
                                    >
                                      <Link
                                        to={subItem.href}
                                        onClick={onClose}
                                        className="flex items-center gap-3 py-2.5 px-3 rounded-lg text-base text-white/70 hover:text-white active:bg-white/5 transition-colors"
                                      >
                                        {subItem.icon && (
                                          <subItem.icon className="w-4 h-4 text-brand-400" />
                                        )}
                                        <span>{subItem.name}</span>
                                      </Link>
                                    </motion.div>
                                  ))
                                : aboutItems.map((subItem, subIndex) => (
                                    <motion.div
                                      key={subItem.href}
                                      initial={{ opacity: 0, x: -10 }}
                                      animate={{ opacity: 1, x: 0 }}
                                      transition={{ delay: subIndex * 0.03, duration: 0.2 }}
                                    >
                                      <Link
                                        to={subItem.href}
                                        onClick={onClose}
                                        className="block py-2.5 px-3 rounded-lg text-base text-white/70 hover:text-white active:bg-white/5 transition-colors"
                                      >
                                        {subItem.name}
                                      </Link>
                                    </motion.div>
                                  ))
                              }
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <Link
                      to={item.href}
                      onClick={onClose}
                      className="flex items-center gap-3 py-3 px-2 rounded-lg active:bg-white/5 transition-colors"
                    >
                      {item.icon && (
                        <span className="text-white/70">
                          {item.icon}
                        </span>
                      )}
                      <span className="text-lg font-light text-white">
                        {item.name}
                      </span>
                    </Link>
                  )}
                </motion.div>
              )
            })}

            {/* Mobile CTA */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: mainNavItems.length * 0.05 + 0.1, duration: 0.3 }}
              className="pt-4 mt-4 border-t border-white/10 space-y-3"
            >
              <Link to="/kontakt" onClick={onClose}>
                <motion.button
                  style={{
                    background: 'linear-gradient(135deg, #e74c63 0%, #d32f4a 50%, #b1233c 100%)',
                    boxShadow: '0 8px 24px rgba(231, 76, 99, 0.4)',
                    border: '1px solid rgba(231, 76, 99, 0.3)',
                  }}
                  className="w-full px-6 py-3.5 text-white rounded-xl font-light text-sm uppercase tracking-[0.15em]"
                  whileTap={{ scale: 0.98 }}
                >
                  Kontakt
                </motion.button>
              </Link>
              
              {/* Close Button */}
              <motion.button
                onClick={onClose}
                className="w-full px-6 py-3.5 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center gap-2 text-white/80 active:bg-white/10 transition-colors"
                whileTap={{ scale: 0.98 }}
                aria-label="Menü schließen"
              >
                <X className="w-5 h-5" />
                <span className="text-sm font-light uppercase tracking-[0.15em]">Schließen</span>
              </motion.button>
            </motion.div>
          </nav>
        </div>
      </motion.div>
    </>
  )
}














